import { FC } from 'react'
import { unstable_useBlocker as useBlocker, useNavigate } from 'react-router-dom'

import { Widget, WidgetSection } from '@factorialco/f0-react/dist/experimental'
import { TimeoffWidgetDocument, TimeoffWidgetQuery } from 'generated/resources/graphql'

import CalendarEventList from 'modules/core/components/CalendarEventList'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import { buildWidget } from 'modules/core/lib/widgets'

const prepareData = (data: TimeoffWidgetQuery) => {
  const pending = data?.timeoff.pending.nodes || []
  const approved = data?.timeoff.approved.nodes || []

  const currentAndUpcoming = approved

  const showMoreCount = Math.max(currentAndUpcoming.length - 3, 0)

  return {
    requested: pending,
    currentAndUpcoming: approved,
    showMoreCount,
  }
}

type PreparedData = ReturnType<typeof prepareData>

const TimeOffWidgetComponent: FC<{
  data: PreparedData
  employeeId: string
  onClickWidget?: () => void
}> = ({ data: { requested, currentAndUpcoming, showMoreCount }, employeeId, onClickWidget }) => {
  const navigate = useNavigate()

  const { navigation } = useNavigation()

  const linkUrl = navigation.employees.id.timeOff.fullPath({
    id: employeeId,
  })

  useBlocker(({ nextLocation }) => {
    if (linkUrl.endsWith(nextLocation.pathname)) {
      onClickWidget?.()
    }
    return false
  })

  const header = useUnsafeMemo(() => {
    const title = i18n.t('insights.widgets.timeoff.title')

    return {
      title,
      link: { title, url: linkUrl },
    }
  }, [linkUrl])

  const handleButtonClick = () => {
    navigate(linkUrl)
  }

  const cta = showMoreCount
    ? i18n.tp('insights.widgets.timeoff.see_more', { count: showMoreCount })
    : undefined

  const action = cta
    ? {
        label: cta,
        onClick: handleButtonClick,
      }
    : undefined

  return (
    <Widget header={header} action={action}>
      {!!requested.length && (
        <WidgetSection title={i18n.t('insights.widgets.timeoff.requested_title')}>
          <CalendarEventList items={requested} />
        </WidgetSection>
      )}
      {!!currentAndUpcoming.length && (
        <WidgetSection title={i18n.t('insights.widgets.timeoff.approved_title')}>
          <CalendarEventList items={currentAndUpcoming} />
        </WidgetSection>
      )}
    </Widget>
  )
}

export const buildTimeOffWidget = () =>
  buildWidget({
    id: 'time-off',
    document: TimeoffWidgetDocument,
    prepareData,
    isEmpty: ({ requested, currentAndUpcoming }) => !requested.length && !currentAndUpcoming.length,
    component: TimeOffWidgetComponent,
    policies: ['readLeaves'],
  })
